import React, { useState, useEffect, useRef } from "react";
import {
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from "recharts";
import ExcelJS from "exceljs";
import { Download, Sheet, FileText } from "lucide-react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  getBankAndBranchLogo,
  getThemeURL,
} from "../../../../../Services/EndPoints";
import { getData, postDataWithBody } from "../../../../../Services/Services";

const RadialProgressChart = ({
  data,
  colors,
  xAxisKey,
  yAxisKeys,
  title,
  item = {},
  containerWidth = 400,
  containerHeight = 400,
  maxValue = null,
}) => {
  console.log("radial Data", data);

  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({
    width: containerWidth,
    height: containerHeight,
  });

  // Add these state variables
  const [logoFromApi, setLogoFromApi] = useState(null);
  const [bankName, setBankName] = useState("");
  const [bankData, setBankData] = useState(null);

  // Add these helper functions
  const getUserProfile = async () => {
    try {
      const profile = await getData(getThemeURL);
      if (profile && profile.length > 0) {
        // setUserProfile(profile[0]);
        if (item.showBankLogo) {
          fetchLogoAndNameFromApi(profile[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      // setUserProfile(null);
    }
  };

  const createPayload = (profile) => {
    let admin_role = "U";

    if (profile.is_admin === true) {
      admin_role = "HO";
    } else if (profile.role_name === "SUPER ADMIN") {
      admin_role = "SA";
    }

    return {
      admin_role: admin_role,
      bank_id: profile.bank_id || null,
      branch_id: profile.branch_id || null,
    };
  };

  const byteArrayToImageUrl = (byteArray) => {
    try {
      const uint8Array = new Uint8Array(byteArray);
      const blob = new Blob([uint8Array], { type: "image/png" });
      const imageUrl = URL.createObjectURL(blob);
      return imageUrl;
    } catch (error) {
      console.error("Error converting byte array to image:", error);
      return null;
    }
  };

  const fetchLogoAndNameFromApi = async (profile) => {
    try {
      const payload = createPayload(profile);
      const response = await postDataWithBody(
        getBankAndBranchLogo,
        payload,
        {}
      );

      if (Array.isArray(response) && response.length > 0) {
        const bankData = response[0];
        setBankData(bankData);

        if (bankData.bankName) {
          setBankName(bankData.bankName);
        } else if (bankData.branchName) {
          setBankName(bankData.branchName);
        }

        if (
          bankData.hasOwnProperty("image") &&
          Array.isArray(bankData.image) &&
          bankData.image.length > 0
        ) {
          const imageUrl = byteArrayToImageUrl(bankData.image);
          setLogoFromApi(imageUrl);
        } else {
          setLogoFromApi(null);
        }
      }
    } catch (error) {
      console.error("Error fetching logo and bank name from API:", error);
      setLogoFromApi(null);
    }
  };

  const formatHeaderText = (text) => {
    if (!text || typeof text !== "string") return text;

    return text
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const downloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Chart Data");

    let currentRow = 1;

    // Add bank logo and name if enabled
    if (item.showBankLogo) {
      try {
        // Add logo if available
        if (logoFromApi) {
          const response = await fetch(logoFromApi);
          const arrayBuffer = await response.arrayBuffer();

          const imageId = workbook.addImage({
            buffer: arrayBuffer,
            extension: "png",
          });

          worksheet.addImage(imageId, {
            tl: { col: 0, row: 0 },
            ext: { width: 100, height: 100 },
          });
        }

        // Add bank name if available (whether logo exists or not)
        if (bankName) {
          const bankNameCell = worksheet.getCell(logoFromApi ? "B1" : "A1");
          bankNameCell.value = bankName;
          bankNameCell.font = {
            bold: true,
            size: parseInt(
              bankData?.banknameFontsize || bankData?.branchNameFontsize || 16
            ),
          };
          bankNameCell.alignment = { vertical: "middle", horizontal: "left" };
        }

        // Set row height and spacing
        if (logoFromApi || bankName) {
          worksheet.getRow(1).height = 75;
          worksheet.getRow(2).height = 25;
          currentRow = 4;
        }
      } catch (error) {
        console.error("Error adding logo/name to Excel:", error);
        currentRow = 1;
      }
    }

    // Add title row if pdfChartTitle is available
    const titleText =
      item.pdfChartTitle !== undefined &&
      item.pdfChartTitle !== null &&
      item.pdfChartTitle.trim() !== ""
        ? item.pdfChartTitle.trim()
        : "";

    if (titleText) {
      worksheet.getCell(`A${currentRow}`).value = titleText;

      const titleRow = worksheet.getRow(currentRow);
      titleRow.font = { bold: true, size: 16 };
      titleRow.alignment = { horizontal: "center" };

      worksheet.mergeCells(`A${currentRow}`, `C${currentRow}`);

      currentRow += 2;
    }

    // Prepare header row
    const yKey = yAxisKeys?.[0] || "value";
    const headerRow = [
      formatHeaderText(xAxisKey || "name"),
      formatHeaderText(item.yAxisLabels?.[0] || yKey),
      "Percentage",
    ];
    worksheet.addRow(headerRow);

    const headerRowIndex = currentRow;
    currentRow++;

    const headerRowObj = worksheet.getRow(headerRowIndex);
    headerRowObj.font = { bold: true };
    headerRowObj.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE0E0E0" },
    };

    // Prepare data rows
    if (!processedData || processedData.length === 0) {
      worksheet.addRow(["NA", "NA", "NA"]);
    } else {
      processedData.forEach((rowObj) => {
        worksheet.addRow([
          rowObj[xAxisKey || "name"] || "NA",
          rowObj.raw_value ?? "NA",
          `${rowObj.percentage_value ?? 0}%`,
        ]);
      });
    }

    worksheet.columns.forEach((column) => {
      column.width = 15;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;

    const now = new Date();
    const istDate = now.toISOString().split("T")[0].replace(/-/g, "");
    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");
    const seconds = now.getSeconds().toString().padStart(2, "0");
    const istTime = `${hours}${minutes}${seconds}`;
    const fileTimestamp = `${istDate}_${istTime}`;
    const fileTitle = (item.title || "chartdata")
      .replace(/\s+/g, "_")
      .toLowerCase();
    const filename = `${fileTitle}_${fileTimestamp}.xlsx`;

    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadPDF = async () => {
    try {
      const doc = new jsPDF("landscape", "pt", "a4");
      let currentY = 50;

      let fontName = "Helvetica";
      doc.setFont(fontName);
      doc.setFontSize(13);

      const pdfChartTitle =
        item.pdfChartTitle !== undefined &&
        item.pdfChartTitle !== null &&
        item.pdfChartTitle.trim() !== ""
          ? item.pdfChartTitle.trim()
          : "";

      // Add bank logo and name if enabled
      if (item.showBankLogo && (logoFromApi || bankName)) {
        try {
          const pageWidth = doc.internal.pageSize.width;
          const centerX = pageWidth / 2;

          if (logoFromApi && bankName) {
            // Both logo and name exist - show side by side
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const img = new Image();
            img.crossOrigin = "anonymous";

            await new Promise((resolve, reject) => {
              img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                resolve();
              };
              img.onerror = reject;
              img.src = logoFromApi;
            });

            const logoDataURL = canvas.toDataURL("image/png");
            const logoWidth = 70;
            const logoHeight = 70;

            doc.setFontSize(16);
            doc.setFont(fontName, "bold");
            const bankNameFontSize =
              bankData?.banknameFontsize || bankData?.branchNameFontsize || 16;
            doc.setFontSize(parseInt(bankNameFontSize));

            const textWidth = doc.getTextWidth(bankName);
            const spacing = 15;
            const totalWidth = logoWidth + spacing + textWidth;
            const startX = centerX - totalWidth / 2;

            const logoX = startX;
            const logoY = currentY;
            const textX = startX + logoWidth + spacing;
            const textY = currentY + logoHeight / 2 + 6;

            doc.addImage(
              logoDataURL,
              "PNG",
              logoX,
              logoY,
              logoWidth,
              logoHeight
            );
            doc.text(bankName, textX, textY);

            currentY += Math.max(logoHeight, 40) + 20;
          } else if (bankName) {
            // Only bank name exists - show centered
            doc.setFont(fontName, "bold");
            const bankNameFontSize =
              bankData?.banknameFontsize || bankData?.branchNameFontsize || 16;
            doc.setFontSize(parseInt(bankNameFontSize));
            doc.text(bankName, centerX, currentY, { align: "center" });
            currentY += 40;
          }

          doc.setFont(fontName, "normal");
          doc.setFontSize(13);
        } catch (error) {
          console.error("Error adding logo/name to PDF:", error);
        }
      }

      // Add title
      const pageWidth = doc.internal.pageSize.width;
      doc.text(pdfChartTitle, pageWidth / 2, currentY, { align: "center" });
      currentY += 30;

      // Prepare headers and data rows
      const yKey = yAxisKeys?.[0] || "value";
      const headers = [
        formatHeaderText(xAxisKey || "name"),
        formatHeaderText(item.yAxisLabels?.[0] || yKey),
        "Percentage",
      ];

      const dataRows = processedData.map((row) => [
        row[xAxisKey || "name"] || "NA",
        (row.raw_value ?? "NA").toString(),
        `${row.percentage_value ?? 0}%`,
      ]);

      doc.autoTable({
        head: [headers],
        body: dataRows,
        startY: currentY,
        theme: "grid",
        headStyles: { fillColor: [59, 130, 246] },
        styles: { fontSize: 10, halign: "center" },
        margin: { left: 40, right: 40 },
        columnStyles: {
          0: {
            halign: (data) => {
              const cellValue = data.cell.raw;
              if (!isNaN(cellValue) && !isNaN(parseFloat(cellValue))) {
                return "center";
              } else {
                return "left";
              }
            },
          },
        },
      });

      // ADD FOOTER SECTION
      const pageHeight = doc.internal.pageSize.height;
      let footerY = pageHeight - 180;

      doc.setFontSize(10);
      doc.text("Letter No.: ", 50, footerY + 100);
      doc.text("Date: ", 50, footerY + 120);
      doc.text("Copy: ", 50, footerY + 140);
      doc.text("XXXX/ ", 550, footerY + 100);
      doc.text("XX(XXXX)(XXXX)/", 550, footerY + 120);
      doc.text("XXXX(XX)/", 550, footerY + 140);

      doc.setFont(fontName, "bold");
      doc.text("[ ]", 550, footerY + 160);
      doc.setFont(fontName, "normal");

      const now = new Date();
      const istDate = now.toISOString().split("T")[0].replace(/-/g, "");
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      const seconds = now.getSeconds().toString().padStart(2, "0");
      const istTime = `${hours}${minutes}${seconds}`;
      const fileTimestamp = `${istDate}_${istTime}`;
      const fileTitle = (item.title || "chart_report")
        .replace(/\s+/g, "_")
        .toLowerCase();
      const filename = `${fileTitle}_${fileTimestamp}.pdf`;

      doc.save(filename);
    } catch (error) {
      alert("Error generating PDF. Please try again.");
      console.error("PDF generation error:", error);
    }
  };

  // useEffect to fetch profile data
  useEffect(() => {
    if (item.showBankLogo) {
      getUserProfile();
    }
  }, [item.showBankLogo]);

  // Cleanup effect for object URLs
  useEffect(() => {
    return () => {
      if (logoFromApi) {
        URL.revokeObjectURL(logoFromApi);
      }
    };
  }, [logoFromApi]);

  // Use ResizeObserver for better dimension tracking
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setDimensions({
          width: Math.max(width, 200),
          height: Math.max(height, 200),
        });
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Update dimensions when props change
  useEffect(() => {
    setDimensions({
      width: Math.max(containerWidth, 200),
      height: Math.max(containerHeight, 200),
    });
  }, [containerWidth, containerHeight]);

  // Calculate percentages based on data values
  const processedData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    const yKey = yAxisKeys?.[0] || "value";

    let calculatedMaxValue = maxValue;
    if (!calculatedMaxValue) {
      calculatedMaxValue = Math.max(...data.map((item) => item[yKey] || 0));
    }

    const maxValueItem = data.reduce((max, item) => {
      const currentValue = item[yKey] || 0;
      const maxCurrentValue = max[yKey] || 0;
      return currentValue > maxCurrentValue ? item : max;
    }, data[0]);

    const filteredData = data.filter((item) => item !== maxValueItem);

    return filteredData.map((item) => {
      const rawValue = item[yKey] || 0;
      const percentage =
        calculatedMaxValue > 0
          ? Math.round((rawValue / calculatedMaxValue) * 100)
          : 0;

      return {
        ...item,
        percentage_value: percentage,
        raw_value: rawValue,
        max_value: calculatedMaxValue,
      };
    });
  }, [data, yAxisKeys, maxValue]);

  if (processedData.length === 0) {
    return (
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            fontSize: "16px",
            color: "#666",
            textAlign: "center",
          }}
        >
          No data to display
        </div>
      </div>
    );
  }

  // Responsive padding and spacing calculations
  const basePadding = Math.min(dimensions.width, dimensions.height) * 0.05;
  const padding = Math.max(10, Math.min(basePadding, 30));
  const labelHeight = Math.max(20, dimensions.height * 0.1);
  const gapBetweenCircles = Math.max(8, dimensions.width * 0.02);

  const availableWidth = dimensions.width - padding * 2;
  const availableHeight = dimensions.height - padding * 2 - labelHeight;

  // Single circle configuration
  if (processedData.length === 1) {
    const centerSize = Math.min(availableWidth, availableHeight) * 0.7;
    const titleFontSize = Math.max(10, centerSize * 0.08);
    const percentageFontSize = Math.max(14, centerSize * 0.12);
    const itemColor = processedData[0]?.color || colors?.[0] || "#1a7a73";

    return (
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: `${padding}px`,
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {/* Download Buttons */}
        {(item.showDownloadExcelChart || item.showDownloadPDFChart) && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 10,
              gap: "10px",
              width: "100%",
            }}
          >
            {item.showDownloadExcelChart && (
              <button
                onClick={downloadExcel}
                style={{
                  padding: "6px 6px",
                  backgroundColor: "#10B981",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                <Download size={16} />
                <Sheet size={16} />
              </button>
            )}
            {item.showDownloadPDFChart && (
              <button
                onClick={downloadPDF}
                style={{
                  padding: "6px 6px",
                  backgroundColor: "#EF4444",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                <Download size={16} />
                <FileText size={16} />
              </button>
            )}
          </div>
        )}

        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            cx="50%"
            cy="50%"
            innerRadius="40%"
            outerRadius="70%"
            barSize={Math.max(6, centerSize * 0.05)}
            data={processedData}
            startAngle={90}
            endAngle={-270}
          >
            <PolarAngleAxis
              type="number"
              domain={[0, 100]}
              angleAxisId={0}
              tick={false}
            />
            <RadialBar
              background
              clockWise
              dataKey="percentage_value"
              cornerRadius={10}
              fill={itemColor}
            />
            <text
              x="50%"
              y="40%"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                fontSize: `${titleFontSize}px`,
                fontWeight: "600",
                fill: itemColor,
              }}
            >
              {processedData[0]?.[xAxisKey || "name"] || title || "Progress"}
            </text>
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                fontSize: `${percentageFontSize}px`,
                fontWeight: "bold",
                fill: itemColor,
              }}
            >
              {`${processedData[0]?.percentage_value || 0}%`}
            </text>
            <text
              x="50%"
              y="60%"
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                fontSize: `${titleFontSize * 0.8}px`,
                fontWeight: "500",
                fill: "#666",
              }}
            >
              {`${processedData[0]?.raw_value || 0} / ${
                processedData[0]?.total_remaining || 0
              }`}
            </text>
          </RadialBarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Multiple circles configuration with responsive sizing
  const numItems = processedData.length;

  const minCircleSize = 40;
  const maxCircleSize = Math.min(availableWidth, availableHeight) * 0.8;

  const itemsPerRow = Math.max(
    1,
    Math.floor(availableWidth / (minCircleSize + gapBetweenCircles))
  );
  const actualItemsPerRow = Math.min(numItems, itemsPerRow);
  const rows = Math.ceil(numItems / actualItemsPerRow);

  const maxCircleWidth =
    (availableWidth - gapBetweenCircles * (actualItemsPerRow - 1)) /
    actualItemsPerRow;
  const maxCircleHeight =
    (availableHeight - gapBetweenCircles * (rows - 1)) / rows;
  const circleSize = Math.max(
    minCircleSize,
    Math.min(maxCircleWidth, maxCircleHeight, maxCircleSize)
  );

  const strokeWidth = Math.max(4, circleSize * 0.08);
  const radius = (circleSize - strokeWidth * 2) / 2;
  const center = circleSize / 2;
  const percentageFontSize = Math.max(8, circleSize * 0.15);
  const valueFontSize = Math.max(6, circleSize * 0.08);
  const labelFontSize = item.legendFontSize
    ? parseInt(item.legendFontSize)
    : Math.max(8, circleSize * 0.09);

  const chartColors = [
    "#1a7a73",
    "#10B981",
    "#8B5CF6",
    "#EF4444",
    "#F59E0B",
    "#EC4899",
    "#14B8A6",
    "#84CC16",
  ];

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: `${padding}px`,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* Download Buttons */}
      {(item.showDownloadExcelChart || item.showDownloadPDFChart) && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 10,
            gap: "10px",
            width: "100%",
          }}
        >
          {item.showDownloadExcelChart && (
            <button
              onClick={downloadExcel}
              style={{
                padding: "6px 6px",
                backgroundColor: "#10B981",
                color: "white",
                border: "none",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              <Download size={16} />
              <Sheet size={16} />
            </button>
          )}
          {item.showDownloadPDFChart && (
            <button
              onClick={downloadPDF}
              style={{
                padding: "6px 6px",
                backgroundColor: "#EF4444",
                color: "white",
                border: "none",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              <Download size={16} />
              <FileText size={16} />
            </button>
          )}
        </div>
      )}

      <div
        style={{
          width: "100%",
          height: `${availableHeight}px`,
          display: "grid",
          gridTemplateColumns: `repeat(${actualItemsPerRow}, 1fr)`,
          gridGap: `${gapBetweenCircles}px`,
          alignItems: "start",
          justifyItems: "center",
          justifyContent: "center",
        }}
      >
        {processedData.map((entry, index) => {
          const percentage = entry.percentage_value || 0;
          const rawValue = entry.raw_value || 0;
          const name = entry[xAxisKey || "name"] || `Item ${index + 1}`;
          const color =
            entry.color ||
            colors?.[index] ||
            chartColors[index % chartColors.length];
          const circumference = 2 * Math.PI * radius;
          const strokeDashoffset = circumference * (1 - percentage / 100);

          return (
            <div
              key={index}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                width: `${circleSize}px`,
                maxWidth: "100%",
              }}
            >
              <div
                style={{
                  width: `${circleSize}px`,
                  height: `${circleSize}px`,
                  position: "relative",
                  marginBottom: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg
                  width={circleSize}
                  height={circleSize}
                  viewBox={`0 0 ${circleSize} ${circleSize}`}
                  style={{
                    transform: "rotate(-90deg)",
                    position: "absolute",
                    top: 0,
                    left: 0,
                  }}
                >
                  <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    stroke="#e5e7eb"
                    strokeWidth={strokeWidth}
                    fill="transparent"
                    strokeLinecap="round"
                  />
                  <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    stroke={color}
                    strokeWidth={strokeWidth}
                    fill="transparent"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    style={{
                      transition: "stroke-dashoffset 0.8s ease-in-out",
                    }}
                  />
                </svg>

                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    lineHeight: "1",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: `${radius * 1.4}px`,
                    height: `${radius * 1.4}px`,
                  }}
                >
                  <div
                    style={{
                      fontSize: `${percentageFontSize}px`,
                      fontWeight: "bold",
                      color: color,
                      marginBottom: "1px",
                    }}
                  >
                    {percentage}%
                  </div>
                  <div
                    style={{
                      fontSize: `${valueFontSize}px`,
                      fontWeight: "500",
                      color: "#666",
                    }}
                  >
                    {rawValue}
                  </div>
                </div>
              </div>

              <div
                style={{
                  fontSize: `${labelFontSize}px`,
                  fontWeight: "600",
                  color: color,
                  textAlign: "center",
                  wordBreak: "break-word",
                  lineHeight: "1.1",
                  width: "100%",
                  maxHeight: `${labelHeight}px`,
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <span
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {name}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RadialProgressChart;
